import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api, { invoicesAPI, settingsAPI } from '../../api';
import {
  ROUTINE_EMAIL_TEMPLATE_KEYS,
  ROUTINE_EMAIL_TEMPLATE_DEFAULTS,
} from '../../components/settings/RoutineEmailsSettings';
import Layout from '../../components/Layout';
import InvoicePaymentForm from '../../components/invoices/InvoicePaymentForm';
import { Button } from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import { stripIframePreviewScripts } from '../../lib/stripIframePreviewScripts';
import { useAuthStore } from '../../store/authStore';
import {
  ArrowLeft,
  Bookmark,
  Check,
  CirclePoundSterling,
  HelpCircle,
  Link2,
  Mail,
  Pencil,
  Printer,
  X,
} from 'lucide-react';

function mergeInvoiceSendTemplates(savedTemplates) {
  const result = {};
  for (const uiKey of Object.keys(ROUTINE_EMAIL_TEMPLATE_KEYS)) {
    const prefKey = ROUTINE_EMAIL_TEMPLATE_KEYS[uiKey];
    const d = ROUTINE_EMAIL_TEMPLATE_DEFAULTS[prefKey];
    const t = savedTemplates?.[prefKey];
    result[uiKey] = {
      subject: t?.subject != null ? String(t.subject) : d.subject,
      body: t?.body != null ? String(t.body) : d.body,
    };
  }
  return result;
}

const DEFAULT_INVOICE_SEND_TEMPLATES = mergeInvoiceSendTemplates(undefined);

const TEMPLATE_TABS = [
  { key: 'standard', label: 'Standard' },
  { key: 'reminder1', label: 'Reminder 1' },
  { key: 'reminder2', label: 'Reminder 2' },
  { key: 'reminder3', label: 'Reminder 3' },
];

function buildContactList(client, apiContacts) {
  const list = [];
  if (!client?.id) return list;

  if (client.email) {
    const clientType = client.clientType || client.client_type;
    const name =
      clientType === 'company'
        ? client.companyName || client.company_name || 'Primary contact'
        : `${client.firstName || client.first_name || ''} ${client.lastName || client.last_name || ''}`.trim() ||
          'Primary contact';
    list.push({
      id: 'primary',
      name,
      email: client.email,
      isPrimary: true,
    });
  }

  (apiContacts || []).forEach((contact) => {
    if (contact.email) {
      list.push({
        id: contact.id,
        name:
          `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || contact.email,
        email: contact.email,
        position: contact.position,
        isPrimary: false,
      });
    }
  });

  return list;
}

export default function InvoiceSend() {
  const { id } = useParams();
  const navigate = useNavigate();
  const iframeRef = useRef(null);
  const user = useAuthStore((s) => s.user);

  const [html, setHtml] = useState('');
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [availableContacts, setAvailableContacts] = useState([]);
  const [sendEmailModalOpen, setSendEmailModalOpen] = useState(false);
  const [sendEmailAddresses, setSendEmailAddresses] = useState([]);
  const [sendEmailLoading, setSendEmailLoading] = useState(false);
  const [sendEmailSuccess, setSendEmailSuccess] = useState('');
  const [templatesMap, setTemplatesMap] = useState(() => DEFAULT_INVOICE_SEND_TEMPLATES);
  const [emailSubject, setEmailSubject] = useState(() => DEFAULT_INVOICE_SEND_TEMPLATES.standard.subject);
  const [emailBody, setEmailBody] = useState(() => DEFAULT_INVOICE_SEND_TEMPLATES.standard.body);
  const [emailTemplateKey, setEmailTemplateKey] = useState('standard');
  const [ccSelf, setCcSelf] = useState(false);
  const [attachPdf, setAttachPdf] = useState(false);
  const [copyLinkState, setCopyLinkState] = useState('idle');

  const [paymentOpen, setPaymentOpen] = useState(false);
  const [flagSentOpen, setFlagSentOpen] = useState(false);
  const [flagSentLoading, setFlagSentLoading] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      const [htmlDoc, inv] = await Promise.all([
        invoicesAPI.getHtml(id, { embed: true }),
        invoicesAPI.getOne(id),
      ]);
      setHtml(stripIframePreviewScripts(typeof htmlDoc === 'string' ? htmlDoc : ''));
      setInvoice(inv);
    } catch (e) {
      setError(e.message || 'Failed to load invoice');
      setHtml('');
      setInvoice(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  /** Refetch HTML + invoice without toggling page-level loading (after send / mark sent / payment). */
  const refreshInvoiceData = useCallback(async () => {
    if (!id) return;
    try {
      const [htmlDoc, inv] = await Promise.all([
        invoicesAPI.getHtml(id, { embed: true }),
        invoicesAPI.getOne(id),
      ]);
      setHtml(stripIframePreviewScripts(typeof htmlDoc === 'string' ? htmlDoc : ''));
      setInvoice(inv);
    } catch (e) {
      setError(e.message || 'Failed to refresh invoice');
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await settingsAPI.get();
        const saved = data?.settings?.preferences?.routineEmails?.templates;
        if (!cancelled) setTemplatesMap(mergeInvoiceSendTemplates(saved));
      } catch (e) {
        console.error('Failed to load routine email templates', e);
        if (!cancelled) setTemplatesMap(mergeInvoiceSendTemplates(undefined));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const t = templatesMap[emailTemplateKey];
    if (t) {
      setEmailSubject(t.subject);
      setEmailBody(t.body);
    }
  }, [templatesMap, emailTemplateKey]);

  const refreshContacts = useCallback(async (client) => {
    if (!client?.id) {
      setAvailableContacts([]);
      return;
    }
    try {
      const response = await api.get(`/clients/${client.id}/contacts`);
      const contacts = response.data.contacts || [];
      const allContacts = buildContactList(client, contacts);
      setAvailableContacts(allContacts);
    } catch (err) {
      console.error('Error fetching contacts:', err);
      setAvailableContacts(buildContactList(client, []));
    }
  }, []);

  useEffect(() => {
    if (invoice?.client) {
      refreshContacts(invoice.client);
    } else {
      setAvailableContacts([]);
    }
  }, [invoice?.client, refreshContacts]);

  const applyEmailTemplate = (key) => {
    if (!templatesMap[key]) return;
    setEmailTemplateKey(key);
  };

  const openEmailModal = () => {
    setSendEmailSuccess('');
    setError('');
    setCcSelf(false);
    setAttachPdf(false);
    applyEmailTemplate('standard');
    const primary = availableContacts.find((c) => c.isPrimary);
    if (primary) {
      setSendEmailAddresses([primary.email]);
    } else if (availableContacts[0]?.email) {
      setSendEmailAddresses([availableContacts[0].email]);
    } else {
      setSendEmailAddresses([]);
    }
    setSendEmailModalOpen(true);
  };

  const toggleRecipientEmail = (email) => {
    setSendEmailAddresses((prev) =>
      prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]
    );
  };

  const handlePrint = () => {
    const win = iframeRef.current?.contentWindow;
    if (win) {
      win.focus();
      win.print();
    }
  };

  const handleSendEmail = async () => {
    if (!id || sendEmailAddresses.length === 0) {
      setError('Please select at least one recipient');
      return;
    }
    if (!emailSubject.trim()) {
      setError('Please enter a subject');
      return;
    }
    if (!emailBody.trim()) {
      setError('Please enter a message');
      return;
    }
    try {
      setSendEmailLoading(true);
      setError('');
      setSendEmailSuccess('');
      for (let i = 0; i < sendEmailAddresses.length; i++) {
        const em = sendEmailAddresses[i];
        const contact = availableContacts.find((c) => c.email === em);
        const recipientFirstName = contact?.name?.trim().split(/\s+/)[0] || undefined;
        await invoicesAPI.sendEmail(id, em.trim(), {
          expiresInDays: 30,
          subject: emailSubject.trim(),
          messageText: emailBody,
          ccSelf: ccSelf && i === 0,
          recipientFirstName,
          attachPdf,
        });
      }
      setSendEmailSuccess(`Invoice link sent to ${sendEmailAddresses.length} recipient(s).`);
      setSendEmailAddresses([]);
      await refreshInvoiceData();
      setTimeout(() => {
        setSendEmailModalOpen(false);
        setSendEmailSuccess('');
      }, 2000);
    } catch (err) {
      setError(err.message || 'Failed to send invoice email');
    } finally {
      setSendEmailLoading(false);
    }
  };

  const handleFlagSent = async () => {
    if (!id) return;
    try {
      setFlagSentLoading(true);
      setError('');
      await invoicesAPI.send(id);
      setFlagSentOpen(false);
      await refreshInvoiceData();
    } catch (err) {
      setError(err.message || 'Could not update invoice status');
    } finally {
      setFlagSentLoading(false);
    }
  };

  const handleCopyPayLink = async () => {
    if (!id) return;
    try {
      setError('');
      const result = await invoicesAPI.generateLink(id, 30);
      const url = `${window.location.origin}/portal/invoice/${result.token}`;
      await navigator.clipboard.writeText(url);
      setCopyLinkState('copied');
      setTimeout(() => setCopyLinkState('idle'), 2500);
    } catch (err) {
      setError(err.message || 'Could not copy payment link');
    }
  };

  const balanceDue = invoice?.balanceDue ?? 0;
  const canPay = balanceDue > 0 && !invoice?.isDeleted;
  const isDraft = (invoice?.status ?? '').toLowerCase() === 'draft';
  const deleted = invoice?.isDeleted;

  return (
    <Layout>
      <div className="bg-[var(--ds-surface-panel)] border-b border-[var(--ds-border-subtle)] shadow-[var(--ds-shadow-card)]">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <h1 className="text-display text-[var(--ds-text-primary)] font-bold truncate">
                {invoice?.invoiceNum ? `Send invoice ${invoice.invoiceNum}` : 'Send invoice'}
              </h1>
              <p className="mt-1 text-meta text-[var(--ds-text-secondary)]">
                Choose how to deliver this invoice, then review the preview below
              </p>
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/tenant/invoices/${id}`)}
                disabled={!id || deleted}
                className="border-[var(--ds-control-field-border)] bg-[var(--ds-control-field-bg)] text-[var(--ds-text-primary)] disabled:opacity-50"
              >
                <Pencil className="h-4 w-4 mr-2" />
                Modify invoice
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrint}
                disabled={!html || loading}
                className="border-[var(--ds-control-field-border)] bg-[var(--ds-control-field-bg)] text-[var(--ds-text-primary)]"
              >
                <Printer className="h-4 w-4 mr-2" />
                Print
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPaymentOpen(true)}
                disabled={!canPay || loading}
                className="border-[var(--ds-control-field-border)] bg-[var(--ds-control-field-bg)] text-[var(--ds-text-primary)] disabled:opacity-50"
              >
                <CirclePoundSterling className="h-4 w-4 mr-2" />
                Log payment
              </Button>
              <Button variant="primary" size="sm" onClick={() => navigate('/tenant/invoices')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to list
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        {error && (
          <div className="p-4 bg-[var(--ds-status-danger-bg)] border border-[var(--ds-status-danger)]/30 rounded-[var(--ds-radius-md)]">
            <p className="text-xs text-[var(--ds-status-danger)]">{error}</p>
          </div>
        )}

        {deleted && (
          <div className="p-4 bg-[var(--ds-control-field-bg)] border border-[var(--ds-border-subtle)] rounded-[var(--ds-radius-md)]">
            <p className="text-sm text-[var(--ds-text-secondary)]">
              This invoice was deleted. You can return to the invoice list to manage active invoices.
            </p>
          </div>
        )}

        {!deleted && (
          <Card className="overflow-hidden bg-[var(--ds-surface-panel)] border-[var(--ds-border-subtle)]">
            <CardContent className="p-0">
              <ul className="divide-y divide-[var(--ds-border-divider)]">
                <li>
                  <button
                    type="button"
                    onClick={openEmailModal}
                    disabled={availableContacts.length === 0}
                    className="flex w-full items-start gap-4 px-4 py-4 sm:px-6 text-left transition-colors hover:bg-[var(--ds-control-field-bg)] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Mail className="h-8 w-8 shrink-0 text-[var(--ds-accent-primary)]" aria-hidden />
                    <div className="min-w-0 flex-1">
                      <span className="text-sm font-semibold text-[var(--ds-accent-primary)]">
                        Send invoice by email
                      </span>
                      <p className="mt-1 text-xs text-[var(--ds-text-muted)]">
                        Email a secure link so your client can view, download, and pay online.
                      </p>
                    </div>
                    <span title="Opens the email composer (secure link; optional CC to yourself).">
                      <HelpCircle className="h-4 w-4 shrink-0 text-[var(--ds-text-muted)]" aria-hidden />
                    </span>
                  </button>
                </li>
                {isDraft && (
                  <li>
                    <button
                      type="button"
                      onClick={() => setFlagSentOpen(true)}
                      disabled={flagSentLoading}
                      className="flex w-full items-start gap-4 px-4 py-4 sm:px-6 text-left transition-colors hover:bg-[var(--ds-control-field-bg)] disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <Bookmark className="h-8 w-8 shrink-0 text-[var(--ds-status-danger)]" aria-hidden />
                      <div className="min-w-0 flex-1">
                        <span className="text-sm font-semibold text-[var(--ds-accent-primary)]">
                          Flag invoice as sent
                        </span>
                        <p className="mt-1 text-xs text-[var(--ds-text-muted)]">
                          Updates your records only: marks the invoice as sent (issued) so it appears in the client
                          portal and outstanding balances.
                        </p>
                      </div>
                      <span title="Draft invoices only.">
                        <HelpCircle className="h-4 w-4 shrink-0 text-[var(--ds-text-muted)]" aria-hidden />
                      </span>
                    </button>
                  </li>
                )}
              </ul>
            </CardContent>
          </Card>
        )}

        <Card className="overflow-hidden bg-[var(--ds-surface-panel)] border-[var(--ds-border-subtle)]">
          <CardContent className="p-0">
            <div className="px-4 py-3 sm:px-6 border-b border-[var(--ds-border-divider)] bg-[var(--ds-control-field-bg)]">
              <h2 className="text-sm font-semibold text-[var(--ds-text-primary)]">Invoice preview</h2>
              <p className="text-xs text-[var(--ds-text-muted)] mt-0.5">Same layout as print and client view</p>
            </div>
            {loading ? (
              <div className="text-center py-16">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--ds-accent-primary)]" />
                <p className="mt-2 text-xs text-[var(--ds-text-muted)]">Loading preview…</p>
              </div>
            ) : html ? (
              <iframe
                ref={iframeRef}
                title={invoice?.invoiceNum ? `Invoice ${invoice.invoiceNum}` : 'Invoice preview'}
                srcDoc={html}
                sandbox="allow-same-origin allow-modals"
                className="w-full min-h-[85vh] border-0 bg-white block"
              />
            ) : (
              <p className="p-8 text-sm text-[var(--ds-text-muted)] text-center">No preview available.</p>
            )}
          </CardContent>
        </Card>
      </div>

      {sendEmailModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-[var(--ds-surface-panel)] rounded-[var(--ds-radius-lg)] max-w-2xl w-full my-8 p-4 sm:p-5 shadow-xl border border-[var(--ds-border-subtle)] text-[12px] leading-normal">
            <div className="flex items-center justify-between mb-4 gap-2">
              <h2 className="text-xs font-bold leading-tight text-gray-900 dark:text-gray-100">
                Send invoice by email
              </h2>
              <button
                type="button"
                onClick={() => {
                  setSendEmailModalOpen(false);
                  setSendEmailAddresses([]);
                  setSendEmailSuccess('');
                  setError('');
                }}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 p-0.5 rounded-md shrink-0"
                aria-label="Close"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </div>

            {sendEmailSuccess ? (
              <div className="py-1">
                <div className="bg-[var(--ds-status-success)]/10 border border-[var(--ds-status-success)]/30 rounded-[var(--ds-radius-md)] p-3">
                  <p className="text-xs text-[var(--ds-status-success)]">{sendEmailSuccess}</p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,6.5rem)_1fr] gap-2 sm:gap-3 sm:items-start">
                  <span className="text-xs font-semibold text-[var(--ds-text-primary)] pt-1.5 sm:pt-2">Send to</span>
                  <div>
                    {availableContacts.length === 0 ? (
                      <p className="text-xs text-gray-500 dark:text-gray-400 italic leading-snug">
                        No email addresses on file for this client. Add contacts in the client record.
                      </p>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {availableContacts.map((contact) => {
                          const selected = sendEmailAddresses.includes(contact.email);
                          return (
                            <button
                              key={contact.id}
                              type="button"
                              onClick={() => toggleRecipientEmail(contact.email)}
                              className={`rounded-full px-2.5 py-1 text-xs font-medium leading-tight transition-colors border ${
                                selected
                                  ? 'bg-emerald-600 border-emerald-600 text-white dark:bg-emerald-600 dark:border-emerald-600'
                                  : 'bg-[var(--ds-control-field-bg)] border-[var(--ds-control-field-border)] text-[var(--ds-text-secondary)] hover:bg-[var(--ds-control-field-bg)]/80'
                              }`}
                            >
                              {contact.name}
                            </button>
                          );
                        })}
                        <button
                          type="button"
                          disabled={!user?.email}
                          title={user?.email ? 'Send a copy to your login email' : 'Your account has no email on file'}
                          onClick={() => setCcSelf((v) => !v)}
                          className={`rounded-full px-2.5 py-1 text-xs font-medium leading-tight transition-colors border disabled:opacity-40 disabled:cursor-not-allowed ${
                            ccSelf
                              ? 'bg-emerald-600 border-emerald-600 text-white dark:bg-emerald-600 dark:border-emerald-600'
                              : 'bg-[var(--ds-control-field-bg)] border-[var(--ds-control-field-border)] text-[var(--ds-text-secondary)] hover:bg-[var(--ds-control-field-bg)]/80'
                          }`}
                        >
                          CC me
                        </button>
                      </div>
                    )}
                  </div>

                  <span className="text-xs font-semibold text-[var(--ds-text-primary)] pt-1.5 sm:pt-2">Subject</span>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3 min-w-0">
                    <input
                      type="text"
                      value={emailSubject}
                      onChange={(e) => setEmailSubject(e.target.value)}
                      className="flex-1 min-w-0 px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--ds-accent-primary)]"
                    />
                    <label className="flex items-center gap-1.5 shrink-0 text-xs text-gray-700 dark:text-gray-300 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={attachPdf}
                        onChange={(e) => setAttachPdf(e.target.checked)}
                        className="h-3.5 w-3.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 accent-[var(--ds-accent-primary)]"
                      />
                      <span title="Also attaches the same PDF as Download/Print (may take a few seconds to generate).">
                        Attach Invoice as PDF
                      </span>
                    </label>
                  </div>

                  <span className="text-xs font-semibold text-[var(--ds-text-primary)] pt-1.5 sm:pt-2">Select template</span>
                  <div className="flex flex-wrap gap-1.5">
                    {TEMPLATE_TABS.map((tab) => (
                      <button
                        key={tab.key}
                        type="button"
                        onClick={() => applyEmailTemplate(tab.key)}
                        className={`rounded-full px-2.5 py-1 text-xs font-medium leading-tight transition-colors border ${
                          emailTemplateKey === tab.key
                            ? 'bg-[var(--ds-accent-primary)] border-[var(--ds-accent-primary)] text-white'
                            : 'bg-[var(--ds-control-field-bg)] border-[var(--ds-control-field-border)] text-[var(--ds-text-secondary)] hover:bg-[var(--ds-control-field-bg)]/80'
                        }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  <span className="text-xs font-semibold text-[var(--ds-text-primary)] sm:pt-1.5">Message</span>
                  <div className="min-w-0">
                    <p className="text-xs text-blue-600 dark:text-blue-400 mb-1 leading-snug">
                      <Link
                        to={`/tenant/settings?tab=routine-emails&expand=${encodeURIComponent(ROUTINE_EMAIL_TEMPLATE_KEYS[emailTemplateKey] || ROUTINE_EMAIL_TEMPLATE_KEYS.standard)}`}
                        className="underline font-medium hover:text-blue-800 dark:hover:text-blue-300"
                      >
                        Edit defaults in Routine emails (Settings)
                      </Link>
                      <span className="text-[var(--ds-text-secondary)]"> — you can still change this message before sending.</span>
                    </p>
                    <textarea
                      value={emailBody}
                      onChange={(e) => setEmailBody(e.target.value)}
                      rows={9}
                      className="w-full px-2 py-1.5 text-xs leading-relaxed border border-gray-300 dark:border-gray-600 rounded-md bg-amber-50 dark:bg-amber-950/25 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--ds-accent-primary)] font-sans"
                    />
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-center justify-center gap-2 pt-1 flex-wrap">
                  <button
                    type="button"
                    onClick={() => {
                      setSendEmailModalOpen(false);
                      setSendEmailAddresses([]);
                      setError('');
                    }}
                    className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-600"
                  >
                    <X className="h-3.5 w-3.5" aria-hidden />
                    Cancel
                  </button>

                  <button
                    type="button"
                    onClick={() => void handleCopyPayLink()}
                    disabled={!id || loading}
                    className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border border-gray-300 dark:border-gray-600 bg-[var(--ds-control-field-bg)] text-[var(--ds-text-primary)] hover:bg-[var(--ds-control-field-bg)]/80 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {copyLinkState === 'copied' ? (
                      <>
                        <Check className="h-3.5 w-3.5 text-[var(--ds-status-success)] shrink-0" aria-hidden />
                        Link copied
                      </>
                    ) : (
                      <>
                        <Link2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
                        Copy payment link
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => void handleSendEmail()}
                    disabled={sendEmailLoading || sendEmailAddresses.length === 0}
                    className="inline-flex items-center justify-center gap-1.5 px-4 py-1.5 rounded-md text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                  >
                    {sendEmailLoading ? 'Sending…' : 'Send'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {flagSentOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--ds-surface-panel)] rounded-[var(--ds-radius-lg)] max-w-md w-full p-6 shadow-xl border border-[var(--ds-border-subtle)]">
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">Mark invoice as sent?</h2>
            <p className="text-sm text-[var(--ds-text-secondary)] mb-6">
              This records the invoice as issued and updates client balances. It does not send an email by itself.
            </p>
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                size="sm"
                type="button"
                onClick={() => setFlagSentOpen(false)}
                className="border-[var(--ds-control-field-border)] bg-[var(--ds-control-field-bg)]"
              >
                Cancel
              </Button>
              <Button variant="primary" size="sm" type="button" disabled={flagSentLoading} onClick={handleFlagSent}>
                {flagSentLoading ? 'Updating…' : 'Mark as sent'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {paymentOpen && invoice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-3xl w-full p-6 shadow-xl border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Log payment</h2>
              <button
                type="button"
                onClick={() => setPaymentOpen(false)}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 text-2xl leading-none"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <InvoicePaymentForm
              invoiceId={invoice.id}
              balanceDue={balanceDue}
              onSuccess={async () => {
                setPaymentOpen(false);
                await refreshInvoiceData();
              }}
              onCancel={() => setPaymentOpen(false)}
            />
          </div>
        </div>
      )}
    </Layout>
  );
}
